<template>
  <!-- prettier-ignore -->
  <div class="product">
    <div
      class="product__name"
      v-text="$t(content).name" />
    <template v-if="personId">
      <div
        class="product-price"
        :class="{ 'product-price--reduced': product.valid && reduction.price > 0 }">
        <template v-if="hasPricing">
          <ovp-price
            :class="{
              product__price: true,
            }"
            :prefix="pricePrefix"
            :price="productPricing.price"
            :suffix="$t('product.currency')"
          />

          <div
          v-if="reduction.price > 0"
          class="product-reduction">
            <div class="reduction-instead">
              {{ $t('product.insteadOf') }}
              <ovp-price :price="reduction.price" />
            </div>

            <div class="reduction__info">
              <template v-for="(r, index) in reduction.labels">
                {{ $t(`info.${r.type}`) }} {{ Math.abs(r.amount) }} {{ $t('product.currency')
                }}<template v-if="reduction.labels.length - 1 !== index">;</template>
              </template>
            </div>
          </div>
        </template>
      </div>

      <template v-if="product.valid || product.selected">
        <div class="product-options">
          <template v-if="hasAccident && hasAccidentToggle">
            <div class="product-criteria product-criteria--half">
              <div class="product__title">
                {{ optionTitles.accident.title }}
                <ovp-icon
                  v-tooltip="optionTitles.accident.tooltip"
                  art="light"
                  html-tooltip
                  name="circle-info" />
              </div>
              <ovp-product-accident
                :option="productPricing"
                @select="selectOption" />
            </div>
          </template>

          <template v-if="hasDuration">
            <div class="product-criteria product-criteria--half">
              <div class="product__title">
                {{ optionTitles.duration.title }}
                <ovp-icon
                  v-tooltip="optionTitles.duration.tooltip"
                  art="light"
                  html-tooltip
                  name="circle-info" />
              </div>
              <ovp-product-duration
                :category-id="categoryId"
                :options="product.prices"
                :product-id="productId"
                :source="source"
                @select="selectOption"
              />
            </div>
          </template>

          <template v-if="hasFranchise">
            <div class="product-criteria product-criteria--full">
              <div class="product__title">
                {{ optionTitles.franchise.title }}
                <ovp-icon
                  v-tooltip="optionTitles.franchise.tooltip"
                  art="light"
                  html-tooltip
                  name="circle-info" />
              </div>
              <ovp-product-franchise
                inline
                :options="product.prices"
                :source="source"
                @select="selectOption" />
            </div>
          </template>
        </div>
      </template>

      <ovp-message
        v-else
        is-html
        :text="messages"
      />

      <div class="product-actions">
        <ovp-product-action
          :person-id="personId"
          :product="product"
          :selected="product.selected"
          @add="add"
          @remove="remove"
          @search="searchMedicalOffice"
        />
      </div>

      <ovp-product-doctor
        v-if="product.medicalOffice"
        :person-id="personId"
        :product="product"
        :source="source"
        @search="searchMedicalOffice"
      />
    </template>

    <ovp-medical-office-search />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import OvpMessage from '@/components/Container/Message'

import OvpProductAction from '@/components/Product/Action'
import OvpProductDoctor from '@/components/Product/Doctor'
import OvpMedicalOfficeSearch from '@/components/Modal/MedicalOfficeSearch'

import { ProductHelper } from '@/helpers'

import { EVENT_MEDICAL_OFFICE_MODAL_OPEN, EVENT_PRODUCT_ADDED, EVENT_PRODUCT_REMOVED } from '@/config/events'

import { BasketMixin, ProductMixin } from '@/mixins'

export default {
  name: 'Product',

  mixins: [BasketMixin, ProductMixin],

  components: {
    OvpMedicalOfficeSearch,
    OvpMessage,
    OvpProductAction,
    OvpProductDoctor,
  },

  props: {
    personId: {
      type: String,
      default: '',
    },

    productId: {
      type: String,
      required: true,
    },

    source: {
      type: String,
      required: true,
    },
  },

  computed: {
    ...mapState('basket', ['contractStartDate']),
    ...mapGetters('basket', ['getCategory', 'getPerson', 'getProduct']),
    ...mapGetters('products', ['getCategoryIdFromProduct', 'getGroupIdFromProduct']),
    ...mapState('products', ['products']),

    categoryId() {
      return this.getCategoryIdFromProduct(this.productId)
    },

    groupId() {
      return this.getGroupIdFromProduct(this.productId)
    },

    hasAccident() {
      return ProductHelper.hasAccident(this.product) && this.optionTitles.accident
    },

    hasAccidentToggle() {
      const dateOfBirth = this.getPerson(this.personId).personData.dateOfBirth
      return ProductHelper.hasAccidentToggle(this.product, this.categoryId, this.contractStartDate, dateOfBirth)
    },

    hasDuration() {
      return ProductHelper.hasDuration(this.product) && this.optionTitles.duration
    },

    hasFranchise() {
      return ProductHelper.hasFranchise(this.product) && this.optionTitles.franchise
    },

    hasOptions () {
      return ProductHelper.hasOptions(this.product)
    },

    optionTitles() {
      const content = ProductHelper.optionTitles(
        this.$te.bind(this),
        this.$t.bind(this),
        this.product.id,
        this.groupId,
        this.categoryId
      )
      return content
    },

    hasPricing() {
      const product = this.getProduct(this.personId, this.productId)
      return this.personId && !!product?.prices
    },

    messages() {
      let content
      const category = this.getCategory(this.personId, this.categoryId)

      content =
        category?.messages
          .map(m => {
            return this.$t(`error.${m.key}`)
          })
          .join('; ')
          .trim() || false

      if (!content) {
        content =
          this.product?.messages
            ?.map(m => {
              return this.$t(`error.${m.key}`)
            })
            .join('; ')
            .trim() || false
      }

      if (!content) {
        content = this.$t(this.$_getContent(this.product.id, 'unavailable'))
      }

      return content
    },

    product() {
      const product = (this.personId && this.getProduct(this.personId, this.productId)) || {}
      const _product = this?.products ? this.products[this.productId] : {}

      return {
        id: this.productId,
        valid: false,
        ..._product,
        ...product,
      }
    },
  },

  methods: {
    ...mapActions('basket', ['setBasket']),

    add() {
      this.$loading.start()
      const mode = this.isUpgrade ? 'ADD' : 'REPLACE'
      ProductHelper.addProductToBasket(this.personId, this.productId, { mode })
        .then(basket => this.updateBasket(basket))
        .then(basket => {
          this.$EventBus.$emit(EVENT_PRODUCT_ADDED, {
            basket,
            categoryId: this.categoryId,
            personId: this.personId,
            productId: this.product.id,
            source: this.source,
          })

          this.$emit('added', this.productId)
          return basket
        })
        .catch(error => this.$notification.error(error))
        .finally(() => {
          this.$loading.end()
        })
    },

    remove() {
      this.$loading.start()

      ProductHelper.removeProductFromBasket(this.personId, this.productId)
        .then(basket => this.updateBasket(basket))
        .then(basket => {
          this.$EventBus.$emit(EVENT_PRODUCT_REMOVED, {
            basket,
            categoryId: this.categoryId,
            personId: this.personId,
            productId: this.product.id,
            source: this.source,
          })

          this.$emit('removed', this.productId)
          return basket
        })
        .catch(error => this.$notification.error(error))
        .finally(() => {
          this.$loading.end()
        })
    },

    searchMedicalOffice() {
      this.$EventBus.$emit(EVENT_MEDICAL_OFFICE_MODAL_OPEN, {
        source: this.source,
        personId: this.personId,
        product: this.product,
      })
    },

    $_getContent(productId, key) {
      const productPath = `content.products.${productId}`
      return this.$te(`${productPath}.${key}`) ? `${productPath}.${key}` : `product.${key}`
    },
  },
}
</script>

<style name="mobile" scoped>
.product {
  color: var(--c-black);

  &__name {
    font-size: 30px;
    line-height: 35px;
    font-weight: bold;
  }

  &-price {
    display: flex;
    flex-direction: column;
    margin: 0 0 30px 0;

    .product__price {
      font-size: 50px;
      line-height: 60px;
    }

    &--reduced {
      .product__price {
        color: var(--c-error);
      }

      .product-reduction {
        display: flex;
        flex-direction: column;
      }
    }
  }

  &-reduction {
    font-size: 15px;

    .reduction {
      &-instead {
        color: var(--c-error);
        font-weight: bold;
      }

      &__info {
        font-weight: bold;
      }
    }
  }

  &-options {
    display: grid;
    grid-gap: 20px;
    margin: 0 0 20px 0;

    .product__title {
      font-weight: bold;
      font-size: 15px;
      line-height: 20px;
      margin: 0 0 10px 0;
    }
  }

  &-actions {
    display: flex;
    width: 100%;

    & >>> .action {
      justify-content: flex-end;
      flex: 1 1 auto;

      .button {
        font-weight: bold;
      }
    }
  }

  .doctor {
    margin: 10px -10px -10px -10px;
  }

  & >>> .message ul {
    padding: 10px 0 10px 40px !important;
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .product {
    &-price {
      flex-direction: row;

      .product__price {
        font-size: 50px;
        line-height: 60px;
      }

      .product-reduction {
        flex-direction: column;
        justify-content: flex-end;
        padding: 0 0 6px 10px;
      }
    }

    &-options {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: repeat(auto-fit, minmax(50px, 1fr));

      .product-criteria {
        &--full {
          grid-column: 1 / span 2;
        }
      }
    }

    &-actions {
      justify-content: flex-end;

      & >>> .action {
        flex: 0 0 auto;
      }
    }
  }
}
</style>
